import { GeneriekBericht, GeneriekBerichtOnderwerp, useGetDossierBerichtenByOnderwerp } from "@/entities/bericht";
import { useAuth, UserRole } from "@/features/auth";
import { Badge, Empty, EmptyDescription, EmptyHeader, ErrorDisplay, Icon } from "@/shared/components";
import { useArchiveVisibility } from "@/shared/hooks";
import { cn, formatDateTimeRelative, mostRecent } from "@/shared/utils";
import { useState } from "react";
import { GeneriekeBerichtenDialog } from "./generieke-berichten-dialog";

type BerichtBalkProps = {
    dossierId: string;
    onderwerp: GeneriekBerichtOnderwerp | GeneriekBerichtOnderwerp[];
};

export function BerichtBalk({ dossierId, onderwerp }: BerichtBalkProps) {
    const isArchived = useArchiveVisibility();
    const { data: berichten, isPending, error } = useGetDossierBerichtenByOnderwerp(dossierId, onderwerp);
    const { hasRole } = useAuth();
    const [open, setOpen] = useState(false);

    if (isPending) return null;
    if (error) return <ErrorDisplay error={error} />;

    const mostRecentBericht = mostRecent(berichten, "verstuurdOp");

    return (
        <>
            <button
                className="w-full shrink-0 cursor-pointer overflow-hidden text-left"
                onClick={() => setOpen(true)}
                data-testid="bericht-balk"
            >
                <div className="bg-elevated-sunken hover:bg-hover text-s px-4 py-2">
                    {onderwerp === "Algemeen" && <h3 className="text-content-default text-xs font-medium">Algemene berichten</h3>}
                    <div className="flex items-center gap-6">
                        {mostRecentBericht ? (
                            <Bericht bericht={mostRecentBericht} showBadge={Array.isArray(onderwerp)} className="flex-1" />
                        ) : (
                            <GeenBerichten />
                        )}

                        <Icon
                            name={hasRole(UserRole.REGIEVOERDER) && !isArchived ? "comment-alt-add" : "comment-alt"}
                            className="text-content-info"
                            title="Bericht opstellen"
                        />
                    </div>
                </div>
            </button>
            {open && <GeneriekeBerichtenDialog dossierId={dossierId} onderwerp={onderwerp} open={open} onOpenChange={setOpen} />}
        </>
    );
}

type BerichtProps = {
    bericht: GeneriekBericht;
    showBadge: boolean;
    className?: string;
};

function Bericht({ bericht, showBadge, className }: BerichtProps) {
    return (
        <div className={cn("flex items-center gap-4 overflow-hidden", className)}>
            <p className="text-content-default flex min-w-0 flex-1 items-baseline gap-2 font-normal">
                <span className="text-content-subtle text-xs font-medium whitespace-nowrap">Laatste bericht:</span>
                {showBadge && (
                    <Badge variant="gray-subtle" className="ring-border ring-1 ring-inset">
                        {bericht.onderwerp}
                    </Badge>
                )}
                <span className="text-s min-w-0 flex-1 truncate" title={bericht.bericht} data-testid="laatste-bericht-text">
                    {bericht.bericht}
                </span>
            </p>
            <time dateTime={bericht.verstuurdOp.toISOString()} className="text-content-subtle text-s whitespace-nowrap">
                {formatDateTimeRelative(bericht.verstuurdOp)}
            </time>
        </div>
    );
}

function GeenBerichten() {
    const isArchived = useArchiveVisibility();
    return (
        <Empty className="items-start">
            <EmptyHeader>
                <EmptyDescription className="text-s">{isArchived ? "Geen" : "Nog geen"} berichten</EmptyDescription>
            </EmptyHeader>
        </Empty>
    );
}
